import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpenText, Wallet } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ContactLinks, fmtDate, gbp } from './shared';

export function CoursesTable({ courses }) {
  return (
    <Card className="p-0 overflow-hidden" data-testid="courses-table">
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2"><BookOpenText className="w-4 h-4 text-gray-500" /><h2 className="font-bold text-gray-900">Courses in range</h2><span className="ml-auto text-xs text-gray-500">{courses.length} course{courses.length === 1 ? '' : 's'}</span></div>
      {courses.length === 0 ? <p className="px-4 py-6 text-sm text-gray-500 italic">No courses dated in this window.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-gray-500 border-b">
              <tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-3 py-2">Course</th><th className="text-right px-3 py-2">Booked</th><th className="text-right px-3 py-2">Attended</th><th className="text-right px-3 py-2">No-shows</th><th className="text-right px-3 py-2">Billed</th><th className="text-right px-3 py-2">Paid</th><th className="text-right px-4 py-2">Owed</th></tr>
            </thead>
            <tbody className="divide-y">
              {courses.map((c) => (
                <tr key={c.course_id} className="hover:bg-gray-50/60" data-testid={`course-row-${c.course_id}`}>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-700">{fmtDate(c.date)}{!c.is_past && <span className="ml-1 text-[10px] font-bold uppercase text-sky-700">upcoming</span>}</td>
                  <td className="px-3 py-2"><Link to={`/staff/training-courses/${c.course_id}`} className="font-semibold text-gray-900 hover:underline">{c.name}</Link> <span className="text-[10px] text-gray-400">{c.brand_label}</span></td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.booked}{c.cancelled ? <span className="text-[10px] text-rose-600 ml-1">({c.cancelled} canc.)</span> : null}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{c.is_past ? c.attended : '—'}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${c.no_shows ? 'text-rose-700 font-semibold' : 'text-gray-500'}`}>{c.is_past ? c.no_shows : '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{gbp(c.billed)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">{gbp(c.paid)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${c.owed > 0 ? 'text-amber-700 font-semibold' : 'text-gray-400'}`}>{c.owed > 0 ? gbp(c.owed) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function OutstandingSection({ outstanding }) {
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? outstanding.rows : outstanding.rows.slice(0, 10);
  return (
    <Card className="p-5" data-testid="outstanding-section">
      <div className="flex items-center gap-2 mb-3">
        <Wallet className="w-5 h-5 text-amber-500" />
        <h2 className="font-bold text-gray-900">Still owed</h2>
        <span className="ml-auto text-sm font-bold text-amber-700 tabular-nums" data-testid="outstanding-total">{gbp(outstanding.total)}</span>
      </div>
      <p className="text-[11px] text-gray-500 mb-2">{gbp(outstanding.total_in_range)} on courses in this window · {gbp(outstanding.total - outstanding.total_in_range)} on courses outside it. Cancelled bookings excluded.</p>
      {outstanding.rows.length === 0 ? <p className="text-sm text-gray-500 italic" data-testid="outstanding-empty">Nothing outstanding.</p> : (
        <ul className="divide-y text-sm">
          {rows.map((o) => (
            <li key={o.registration_id} className="py-1.5 flex items-center gap-3" data-testid={`outstanding-row-${o.registration_id}`}>
              <div className="min-w-0 flex-1">
                <Link to={`/staff/training-courses/${o.course_id}`} className="font-semibold text-gray-900 hover:underline">{o.name}</Link>
                <span className="text-gray-500 text-xs"> · {o.course} · {fmtDate(o.date)}</span>
              </div>
              <ContactLinks row={o} />
              <span className="tabular-nums font-bold text-amber-700 w-24 text-right">{gbp(o.owed)}</span>
            </li>
          ))}
        </ul>
      )}
      {outstanding.rows.length > 10 && (
        <button type="button" onClick={() => setShowAll((s) => !s)} className="mt-2 text-xs text-emerald-700 hover:underline" data-testid="outstanding-toggle">{showAll ? 'Show fewer' : `Show all ${outstanding.rows.length}`}</button>
      )}
    </Card>
  );
}
